"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { ProUpsellModal } from "./pro-upsell-modal";
import { clientTrack } from "@/lib/analytics/client";
import type { Track } from "@/lib/analytics/track";
import { emitPaywallHit, emitUpgradeClicked, type PaywallTrigger } from "@/lib/analytics/paywall";
import { webAppContext } from "@/lib/analytics/surface";
import type { AnalyticsContext } from "@/lib/analytics/envelope";
import type { PaywallPlanPresentation } from "@/lib/billing/paywallPresentation";

/**
 * The generic Pro modal's trigger mechanism (MAP-117, D2). Exactly ONE
 * `<ProUpsellModal>` is mounted per provider (see the render below); every
 * Pro-gated surface in the tree calls `useProUpsell().openProUpsell(...)`
 * to open that single instance rather than mounting its own.
 */

export interface OpenProUpsellOptions {
  /** The enumerated paid trigger (analytics/paywall.ts) — never a free-form string. */
  trigger: PaywallTrigger;
  /** The paid plan code this gate would upgrade to, if known (enrichment only). */
  planCode?: string;
  /** On-device draft restored after anonymous auth; never sent to analytics. */
  draftId?: string;
  /** The gated action Stripe should resume after payment. */
  intendedAction?: string;
}

export interface ProUpsellContextValue {
  openProUpsell: (options: OpenProUpsellOptions) => void;
}

const ProUpsellContext = createContext<ProUpsellContextValue | null>(null);

/**
 * Pure: the `emitPaywallHit` payload for a given `openProUpsell()` call.
 * Extracted so it's unit-testable without React/DOM (this project's tests
 * run in the `node` vitest environment — no jsdom/testing-library, see
 * site-nav-auth.test.tsx). `usage` is always 0 — this is a binary
 * capability gate, not a count soft-block (those keep the real usage number
 * via the instrumented `Paywall`). `quotaType` doubles as the trigger string
 * itself: the generic modal fronts several capabilities, some
 * (`share_password`/`data_export`, per D1) with no storage-keyed
 * `QuotaLimit` at all, so there is no single limit to look up a quota_type
 * from the way `quotaLimitToQuotaType` does for the count-limit Paywall.
 */
export function paywallHitPayload({
  trigger,
  planCode,
}: OpenProUpsellOptions): { trigger: PaywallTrigger; quotaType: string; usage: number; planCode?: string } {
  return { trigger, quotaType: trigger, usage: 0, planCode };
}

export interface ProModalProviderProps {
  children: ReactNode;
  /** Session ids used to build the client analytics context. */
  anonSessionId?: string;
  userId?: string;
  /** Emission seam (defaults to the browser sink); tests inject a mock. */
  track?: Track;
  /** Analytics context override (defaults to a web_app context from the ids). */
  ctx?: AnalyticsContext;
  /** Full pricing/checkout page the modal's "Upgrade to Pro" CTA links to. */
  pricingHref?: string;
  /** Listed paid plans resolved from the request-time plan table, cheapest first. */
  plans?: PaywallPlanPresentation[];
  /** White-label brand name resolved by the server layout. */
  brandName?: string;
}

export function resolveProUpsellPlan(
  plans: PaywallPlanPresentation[],
  trigger: PaywallTrigger,
  requestedCode?: string,
): PaywallPlanPresentation | null {
  const targetCode = trigger === "satellite_basemap" || trigger === "organize" ? "lite" : "pro";
  return (
    plans.find((plan) => plan.code === targetCode) ??
    plans.find((plan) => plan.code === requestedCode) ??
    plans[0] ??
    null
  );
}

/**
 * Mounts the ONE generic Pro-upsell modal for a layout tree and exposes
 * `useProUpsell()` to everything under it. Opening fires the canonical
 * `paywall_hit { trigger, quota_type, usage }` so the per-trigger conversion
 * funnel (`listPaywallTriggers`) keeps working even though every trigger
 * renders identical modal copy. `usage` is always 0 here — this is a binary
 * capability gate (visibility/password/exports/...), not a count soft-block
 * (those keep firing paywall_hit from the instrumented `Paywall`, with a real
 * usage number).
 */
export function ProModalProvider({
  children,
  anonSessionId,
  userId,
  track,
  ctx,
  pricingHref,
  plans = [],
  brandName,
}: ProModalProviderProps) {
  const [state, setState] = useState<{ open: boolean; options: OpenProUpsellOptions | null }>({
    open: false,
    options: null,
  });
  const [email, setEmail] = useState("");
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const resolvedTrack = useMemo<Track>(() => track ?? clientTrack, [track]);
  const resolvedCtx = useMemo<AnalyticsContext>(
    () => ctx ?? webAppContext({ anonSessionId, userId }),
    [ctx, anonSessionId, userId],
  );

  const openProUpsell = useCallback(
    (options: OpenProUpsellOptions) => {
      const targetPlan = resolveProUpsellPlan(plans, options.trigger, options.planCode);
      const resolvedOptions = {
        ...options,
        planCode: targetPlan?.code ?? options.planCode,
      };
      setState({ open: true, options: resolvedOptions });
      // Fired directly here (not from a mount effect keyed on `open`) so it
      // runs exactly once per openProUpsell() call, with no re-render/re-fire
      // risk — see Paywall's `hitFired` ref for the pattern this replaces
      // when the modal owns its own open state instead of taking it as a prop.
      emitPaywallHit(resolvedTrack, resolvedCtx, paywallHitPayload(resolvedOptions));
    },
    [resolvedTrack, resolvedCtx, plans],
  );

  const value = useMemo<ProUpsellContextValue>(() => ({ openProUpsell }), [openProUpsell]);
  const recommendedPlan =
    plans.find((plan) => plan.code === state.options?.planCode) ??
    (state.options ? resolveProUpsellPlan(plans, state.options.trigger, state.options.planCode) : null) ??
    null;

  function handleClose() {
    setState((s) => ({ ...s, open: false }));
    setEmail("");
    setCaptureError(null);
    setCapturing(false);
  }

  async function handleAnonymousUpgrade() {
    const normalizedEmail = email.trim().toLowerCase();
    const options = state.options;
    if (!normalizedEmail || !options || !anonSessionId) {
      setCaptureError("Enter your email to continue.");
      return;
    }
    const checkoutAttemptId = crypto.randomUUID();
    emitUpgradeClicked(resolvedTrack, resolvedCtx, {
      trigger: options.trigger,
      planCode: options.planCode,
      checkoutAttemptId,
    });
    setCaptureError(null);
    setCapturing(true);
    try {
      const res = await fetch("/api/billing/paywall-lead", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          trigger_event: options.trigger,
          quota_type: options.trigger,
          plan_code: options.planCode,
          intended_action: options.intendedAction ?? options.trigger,
          draft_id: options.draftId,
          usage: 0,
          checkout_attempt_id: checkoutAttemptId,
        }),
      });
      const data = res.ok ? ((await res.json().catch(() => null)) as { lead_id?: string } | null) : null;
      if (!data?.lead_id) throw new Error("We couldn't save that email. Try again.");
      const params = new URLSearchParams({ upgrade: data.lead_id });
      if (options.draftId) params.set("restore", options.draftId);
      window.location.href = `/sign-up?${params.toString()}`;
    } catch (err) {
      setCapturing(false);
      setCaptureError(err instanceof Error ? err.message : "We couldn't continue. Try again.");
    }
  }

  return (
    <ProUpsellContext.Provider value={value}>
      {children}
      <ProUpsellModal
        open={state.open}
        onClose={handleClose}
        pricingHref={pricingHref}
        trigger={state.options?.trigger}
        plan={recommendedPlan}
        brandName={brandName}
        anonymousUpgrade={
          !userId
            ? {
                email,
                onEmailChange: (value) => {
                  setEmail(value);
                  if (captureError) setCaptureError(null);
                },
                onSubmit: () => void handleAnonymousUpgrade(),
                loading: capturing,
                error: captureError,
                signInHref: state.options?.draftId
                  ? `/sign-in?restore=${encodeURIComponent(state.options.draftId)}`
                  : "/sign-in",
              }
            : undefined
        }
      />
    </ProUpsellContext.Provider>
  );
}

/**
 * Returns `{ openProUpsell }`. Throws when called outside a
 * `<ProModalProvider>` rather than silently no-op-ing: a caller reaching for
 * this hook always intends to open the modal, so swallowing that intent
 * would hide a real mounting bug (e.g. a gate rendered on a route that never
 * mounted `ProModalMount`) behind a click that quietly does nothing.
 */
export function useProUpsell(): ProUpsellContextValue {
  const value = useContext(ProUpsellContext);
  if (!value) {
    throw new Error("useProUpsell() must be called within a <ProModalProvider>.");
  }
  return value;
}
